import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@aircall/ds";
import { EllipsisVertical } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";

import { EvaluationComment } from "../EvaluationComment";
import { EvaluationPercentage } from "../EvaluationPercentage";

import { Locale, formatDistanceToNow } from "date-fns";
import { de, enUS, es, fr, it, nb } from "date-fns/locale";
import { EvaluationHeadingProps } from ".";

const localeMap: Record<string, Locale> = {
  en: enUS,
  fr: fr,
  de: de,
  es: es,
  it: it,
  nb: nb,
};

export function EvaluationHeading({
  title,
  disabled,
  totalPoints,
  totalMaximumPoints,
  comment,
  description,
  lastReviewAt,
  lastReviewName,
  canEdit,
  canDelete,
  onClickEdit,
  onClickDelete,
}: EvaluationHeadingProps) {
  const { t, i18n } = useTranslation();
  const locale = localeMap[i18n.language];

  return (
    <div className="flex flex-col gap-2 bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-xl font-bold">{title}</p>
          {Boolean(description) && (
            <p className="text-base font-normal">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <EvaluationPercentage
            points={totalPoints}
            maximumPoints={totalMaximumPoints}
            disabled={disabled}
          />
          {(canEdit || canDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    data-test="action-evaluation-button"
                  />
                }
              >
                <EllipsisVertical className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent data-test="action-dropdown" align="end">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={onClickEdit}
                    data-test="edit-evaluation-button"
                  >
                    {t("evaluation.edit_evaluation")}
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem
                    onClick={onClickDelete}
                    data-test="delete-evaluation-button"
                  >
                    {t("evaluation.remove")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {Boolean(comment) && <EvaluationComment>{comment}</EvaluationComment>}
      {lastReviewAt && lastReviewName && (
        <p className="text-xs font-normal">
          {t("evaluation.last_reviewed", {
            distanceToNow: formatDistanceToNow(new Date(lastReviewAt), {
              addSuffix: true,
              locale,
            }),
            name: lastReviewName,
          })}
        </p>
      )}
    </div>
  );
}
